﻿/* Kuddy Beauty Quiz Engine — vanilla JS, hybrid mode (theme config + worker results) */
(function () {
  'use strict';

  let quizCatalogPromise = null;

  function esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatPrice(amount, currency) {
    const n = parseFloat(amount || 0);
    if (!currency || currency === 'NGN') {
      return '\u20A6' + n.toLocaleString('en-NG', { maximumFractionDigits: 0 });
    }
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);
    } catch (_) {
      return currency + ' ' + n.toFixed(0);
    }
  }

  function uid() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  function isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '');
  }

  function loadQuizCatalog(src) {
    if (!quizCatalogPromise) {
      quizCatalogPromise = fetch(src).then(function (res) {
        if (!res.ok) throw new Error('This quiz isn\'t available right now. Please refresh and try again.');
        return res.json();
      });
    }
    return quizCatalogPromise;
  }

  var QUIZ_BUDGET_RANGES = {
    budget: [0, 5000],
    mid: [5000, 15000],
    premium: [15000, Infinity]
  };

  function matchesQuizBudget(price, budgetKey, quizHandle) {
    if (!budgetKey) return true;
    var range = QUIZ_BUDGET_RANGES[budgetKey];
    if (!range) return true;
    var n = parseFloat(price || 0);
    if (!Number.isFinite(n) || n <= 0) return false;
    return n >= range[0] && n < range[1];
  }

  function mapStorefrontProduct(p) {
    const variant = (p.variants && p.variants[0]) || null;
    const image = (p.images && p.images[0]) || null;
    return {
      id: p.id,
      handle: p.handle,
      title: p.title,
      tags: p.tags || [],
      image: image ? image.src : '',
      imageAlt: (image && image.alt) || p.title,
      price: variant ? String(variant.price) : '0',
      currency: 'NGN',
      variantId: variant ? String(variant.id) : '',
      url: '/products/' + p.handle,
    };
  }

  function buildProfileText(quizHandle, answers) {
    const a = answers || {};
    if (quizHandle === 'skin-match') {
      const parts = [];
      if (a.skin_type) parts.push(a.skin_type + ' skin');
      if (a.concern) parts.push(a.concern.replace('-', ' '));
      return parts.length
        ? 'Based on your ' + parts.join(' with ') + ' profile, here are your perfect matches.'
        : 'Here are your personalised skincare matches.';
    }
    if (quizHandle === 'shade-finder') {
      return a.product_type
        ? 'Here are the best ' + a.product_type + ' shades for your ' + (a.skin_tone || 'skin tone') + '.'
        : 'Here are your best shade matches.';
    }
    if (quizHandle === 'scent-finder') {
      return a.scent_family
        ? 'Your love for ' + a.scent_family + ' scents leads to these perfect picks.'
        : 'Here are fragrances matched to your personality.';
    }
    if (quizHandle === 'wash-day') {
      return a.hair_type
        ? 'Your ' + a.hair_type.replace('-', ' ') + ' wash day kit starts here.'
        : "Here's your personalised wash day routine.";
    }
    return 'Here are your personalised product matches.';
  }

  class KuddyQuiz {
    constructor(el) {
      this.el = el;
      this.handle = (el.dataset.quiz || 'skin-match').trim();
      this.configSrc = (el.dataset.quizConfigSrc || '').trim();
      this.proxyBase = (el.dataset.proxyBase || '/apps/kuddy-dev').replace(/\/$/, '');
      this.sessionId = this._loadSession();
      this.config = null;
      this.steps = [];
      this.fallbacks = {};
      this.stepIndex = 0;
      this.answers = {};
      this.history = [];
    }

    _loadSession() {
      try {
        const stored = localStorage.getItem('kuddy_quiz_session_' + this.handle);
        if (stored) return stored;
      } catch (_) {}
      const id = uid();
      try { localStorage.setItem('kuddy_quiz_session_' + this.handle, id); } catch (_) {}
      return id;
    }

    async init() {
      this._setHTML(this._tplLoading('Loading your quiz\u2026'));
      try {
        await this._loadConfig();
      } catch (err) {
        this._setHTML(this._tplError(err.message));
        return;
      }
      this._track('start');
      this._renderStep(0);
    }

    async _loadConfig() {
      if (this.configSrc) {
        try {
          const catalog = await loadQuizCatalog(this.configSrc);
          this.fallbacks = catalog.fallbacks || {};
          const quiz = catalog.quizzes && catalog.quizzes[this.handle];
          if (!quiz) throw new Error('This quiz isn\'t available right now.');
          this.config = {
            handle: this.handle,
            title: quiz.title,
            description: quiz.description,
            step_count: quiz.step_count,
          };
          this.steps = quiz.steps || [];
          if (!this.steps.length) throw new Error('Quiz has no steps configured.');
          return;
        } catch (_) {
          /* fall through to proxy */
        }
      }
      await this._loadConfigFromProxy();
    }

    async _loadConfigFromProxy() {
      const res = await fetch(
        this.proxyBase + '/quiz-config?quiz=' + encodeURIComponent(this.handle)
      );
      if (!res.ok) throw new Error('This quiz isn\'t available right now.');
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Quiz unavailable.');
      this.config = data.quiz;
      this.steps = data.steps || [];
      if (!this.steps.length) throw new Error('Quiz has no steps configured.');
    }

    _renderStep(index) {
      this.stepIndex = index;
      const step = this.steps[index];
      const total = this.steps.length;
      const pct = Math.round((index / total) * 100);
      const selected = this.answers[step.field_key];

      const optionsHTML = step.options.map(function (opt, i) {
        const isSelected = selected && selected.value === opt.value;
        return (
          '<button' +
          ' class="kq-option' + (isSelected ? ' kq-option--selected' : '') + '"' +
          ' data-step="' + index + '"' +
          ' data-opt="' + i + '"' +
          ' type="button"' +
          ' aria-pressed="' + (isSelected ? 'true' : 'false') + '">' +
          '<span class="kq-opt-label">' + esc(opt.label) + '</span>' +
          (opt.description ? '<span class="kq-opt-desc">' + esc(opt.description) + '</span>' : '') +
          '</button>'
        );
      }).join('');

      this._setHTML(
        '<div class="kq-root">' +
          '<div class="kq-header">' +
            '<div class="kq-step-count">' + (index + 1) + ' of ' + total + '</div>' +
            '<div class="kq-progress" role="progressbar" aria-valuenow="' + pct + '" aria-valuemin="0" aria-valuemax="100">' +
              '<div class="kq-progress-fill" style="width:' + pct + '%"></div>' +
            '</div>' +
          '</div>' +
          '<div class="kq-body">' +
            '<h2 class="kq-question">' + esc(step.question) + '</h2>' +
            '<div class="kq-options">' + optionsHTML + '</div>' +
          '</div>' +
          '<div class="kq-footer">' +
            (index > 0
              ? '<button class="kq-btn kq-btn--back" data-action="back" type="button">&#8592; Back</button>'
              : '<span></span>') +
            '<button' +
              ' class="kq-btn kq-btn--next' + (selected ? '' : ' kq-btn--disabled') + '"' +
              ' data-action="next"' +
              ' type="button"' +
              (selected ? '' : ' disabled') + '>' +
              (index < total - 1 ? 'Continue' : 'See my matches') +
            '</button>' +
          '</div>' +
        '</div>'
      );

      this._attachStepListeners();
    }

    _renderEmail() {
      this._setHTML(
        '<div class="kq-root kq-email-screen">' +
          '<div class="kq-email-icon">&#10024;</div>' +
          '<h2 class="kq-email-heading">Your matches are ready!</h2>' +
          '<p class="kq-email-sub">Enter your email to get your personalised results sent to you \u2014 or skip to view them now.</p>' +
          '<div class="kq-email-form">' +
            '<label class="visually-hidden" for="kq-email-input">Email address (optional)</label>' +
            '<input id="kq-email-input" type="email" placeholder="Your email address (optional)" class="kq-email-input" autocomplete="email" inputmode="email" />' +
            '<p class="kq-email-error" id="kq-email-error" style="display:none;color:#c0392b;font-size:0.8rem;margin-top:0.25rem;"></p>' +
          '</div>' +
          '<div class="kq-email-actions">' +
            '<button class="kq-btn kq-btn--primary" data-action="results-with-email" type="button">View my matches</button>' +
            '<button class="kq-btn kq-btn--ghost" data-action="results-skip" type="button">Skip &rarr;</button>' +
          '</div>' +
          '<button class="kq-btn kq-btn--back" data-action="back-to-quiz" type="button" style="margin-top:1rem;">&#8592; Change my answers</button>' +
        '</div>'
      );

      const self = this;
      this.el.querySelector('[data-action="results-with-email"]').addEventListener('click', function () {
        const input = self.el.querySelector('#kq-email-input');
        const errEl = self.el.querySelector('#kq-email-error');
        const email = (input && input.value || '').trim();
        if (email && !isValidEmail(email)) {
          errEl.textContent = 'Please enter a valid email address.';
          errEl.style.display = 'block';
          input.focus();
          return;
        }
        self._loadResults(email);
      });

      this.el.querySelector('[data-action="results-skip"]').addEventListener('click', function () {
        self._loadResults('');
      });

      this.el.querySelector('[data-action="back-to-quiz"]').addEventListener('click', function () {
        self._renderStep(self.steps.length - 1);
      });
    }

    async _loadResults(email) {
      this._setHTML(this._tplLoading('Finding your perfect matches\u2026'));

      const allTags = Object.values(this.answers).flatMap(function (a) { return a.tags || []; });
      const answerValues = {};
      for (const key of Object.keys(this.answers)) {
        answerValues[key] = this.answers[key].value;
      }

      let data;
      let usedFallback = false;

      try {
        data = await this._fetchWorkerResults(allTags, answerValues, email);
      } catch (_) {
        try {
          data = await this._loadFallbackResults(answerValues, allTags);
          usedFallback = true;
        } catch (err) {
          this._setHTML(this._tplError(err.message || 'Could not load recommendations.', true));
          return;
        }
      }

      try { localStorage.setItem('kuddy_quiz_session', this.sessionId); } catch (_) {}
      try { localStorage.setItem('kuddy_quiz_handle', this.handle); } catch (_) {}
      await this._persistCartSession();

      const products = data.products || [];
      this._track('complete', this._funnelPayload());
      this._renderResults(products, data.profile_text || '');
    }

    async _fetchWorkerResults(allTags, answerValues, email) {
      const res = await fetch(this.proxyBase + '/quiz-results', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quiz: this.handle,
          tags: allTags,
          answers: answerValues,
          email: email || '',
          session_id: this.sessionId,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Could not load recommendations.');
      return data;
    }

    _resolveCollectionHandle(answerValues) {
      const rules = this.fallbacks[this.handle];
      if (!rules) return 'all';
      let handle = rules.default || 'all';
      const fields = rules.fields || {};
      for (let i = 0; i < this.steps.length; i++) {
        const key = this.steps[i].field_key;
        const val = answerValues[key];
        if (val && fields[key] && fields[key][val]) {
          handle = fields[key][val];
        }
      }
      return handle;
    }

    async _loadFallbackResults(answerValues, allTags) {
      const self = this;
      const collHandle = this._resolveCollectionHandle(answerValues);
      const res = await fetch(
        '/collections/' + encodeURIComponent(collHandle) + '/products.json?limit=50'
      );
      if (!res.ok) throw new Error('Could not load products from collection.');
      const payload = await res.json();
      let products = (payload.products || []).map(mapStorefrontProduct);

      if (answerValues.budget) {
        products = products.filter(function (p) {
          return matchesQuizBudget(p.price, answerValues.budget, self.handle);
        });
      }

      products = products.slice(0, 6);

      if (allTags.length && products.length > 1) {
        const tagSet = allTags.map(function (t) { return String(t).toLowerCase(); });
        products = products.slice().sort(function (a, b) {
          function score(p) {
            const tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
            return tagSet.reduce(function (n, t) {
              return n + (tags.some(function (tag) { return tag.indexOf(t) !== -1 || t.indexOf(tag) !== -1; }) ? 1 : 0);
            }, 0);
          }
          return score(b) - score(a);
        });
      }

      return {
        products: products,
        profile_text: buildProfileText(this.handle, answerValues),
      };
    }

    _renderResults(products, profileText) {
      const quizTitle = (this.config && this.config.title) || 'Your results';

      const cardsHTML = products.length
        ? products.map(function (p) {
            return (
              '<div class="kq-product-card">' +
                '<a href="' + esc(p.url) + '" class="kq-product-img-link">' +
                  (p.image
                    ? '<img src="' + esc(p.image) + '" alt="' + esc(p.imageAlt) + '" class="kq-product-img" loading="lazy">'
                    : '<div class="kq-product-img-placeholder"></div>') +
                '</a>' +
                '<div class="kq-product-body">' +
                  '<a href="' + esc(p.url) + '" class="kq-product-title">' + esc(p.title) + '</a>' +
                  '<div class="kq-product-price">' + esc(formatPrice(p.price, p.currency)) + '</div>' +
                  '<div class="kq-product-actions">' +
                    '<a href="' + esc(p.url) + '" class="kq-card-btn kq-card-btn--view">View product</a>' +
                    (p.variantId
                      ? '<button class="kq-card-btn kq-card-btn--cart" data-action="add-to-cart" data-variant="' + esc(p.variantId) + '" data-handle="' + esc(p.handle || '') + '" data-title="' + esc(p.title) + '" type="button">Add to bag</button>'
                      : '') +
                  '</div>' +
                '</div>' +
              '</div>'
            );
          }).join('')
        : '<p class="kq-no-results">We\u2019re adding more products to match this profile soon \u2014 check back shortly!</p>';

      this._setHTML(
        '<div class="kq-root kq-results-screen">' +
          '<div class="kq-results-header">' +
            '<div class="kq-results-badge">&#10024; Results from: ' + esc(quizTitle) + '</div>' +
            (profileText ? '<p class="kq-profile-text">' + esc(profileText) + '</p>' : '') +
          '</div>' +
          '<div class="kq-product-grid">' + cardsHTML + '</div>' +
          '<div class="kq-results-footer">' +
            '<button class="kq-btn kq-btn--outline" data-action="retake" type="button">Retake quiz</button>' +
            '<a href="/collections/all" class="kq-btn kq-btn--ghost">Shop all products &rarr;</a>' +
          '</div>' +
        '</div>'
      );

      this._attachResultListeners();
    }

    _attachStepListeners() {
      const self = this;
      const step = this.steps[this.stepIndex];

      this.el.querySelectorAll('.kq-option').forEach(function (btn) {
        btn.addEventListener('click', function () {
          const optIdx = parseInt(btn.dataset.opt, 10);
          const option = step.options[optIdx];
          if (!option) return;

          self.el.querySelectorAll('.kq-option').forEach(function (b) {
            b.classList.remove('kq-option--selected');
            b.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('kq-option--selected');
          btn.setAttribute('aria-pressed', 'true');

          self.answers[step.field_key] = {
            label: option.label,
            value: option.value,
            tags: option.tags || [],
          };

          const nextBtn = self.el.querySelector('[data-action="next"]');
          if (nextBtn) {
            nextBtn.disabled = false;
            nextBtn.classList.remove('kq-btn--disabled');
          }

          setTimeout(function () {
            if (self.stepIndex < self.steps.length - 1) {
              self.history.push(self.stepIndex);
              self._renderStep(self.stepIndex + 1);
            } else {
              self._renderEmail();
            }
          }, 320);
        });
      });

      const nextBtn = this.el.querySelector('[data-action="next"]');
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if (nextBtn.disabled) return;
          if (self.stepIndex < self.steps.length - 1) {
            self.history.push(self.stepIndex);
            self._renderStep(self.stepIndex + 1);
          } else {
            self._renderEmail();
          }
        });
      }

      const backBtn = this.el.querySelector('[data-action="back"]');
      if (backBtn) {
        backBtn.addEventListener('click', function () {
          const prev = self.history.pop();
          if (prev !== undefined) self._renderStep(prev);
        });
      }
    }

    _attachResultListeners() {
      const self = this;

      this.el.querySelectorAll('[data-action="add-to-cart"]').forEach(function (btn) {
        btn.addEventListener('click', async function () {
          const variantId = btn.dataset.variant;
          const productHandle = btn.dataset.handle || '';
          if (!variantId) return;
          btn.disabled = true;
          btn.textContent = 'Adding\u2026';
          try {
            await self._addToCart(variantId, productHandle);
            btn.textContent = 'Added \u2714';
            btn.classList.add('kq-card-btn--added');
          } catch (_) {
            btn.textContent = 'Add to bag';
            btn.disabled = false;
          }
        });
      });

      const retake = this.el.querySelector('[data-action="retake"]');
      if (retake) {
        retake.addEventListener('click', function () {
          self.answers = {};
          self.history = [];
          self.sessionId = uid();
          try { localStorage.setItem('kuddy_quiz_session_' + self.handle, self.sessionId); } catch (_) {}
          try { localStorage.removeItem('kuddy_quiz_session'); } catch (_) {}
          try { localStorage.removeItem('kuddy_quiz_handle'); } catch (_) {}
          self._track('retake');
          self._renderStep(0);
        });
      }
    }

    async _persistCartSession() {
      const journey = window.KuddyJourney;
      const attrs = {
        quiz_session: this.sessionId,
        quiz_type: this.handle,
        kuddy_last_touch: 'quiz_complete',
      };
      if (journey && typeof journey.getJourneyId === 'function') {
        attrs.kuddy_journey_id = journey.getJourneyId();
      }
      try {
        if (journey && typeof journey.syncCartAttributes === 'function') {
          await journey.syncCartAttributes(attrs);
          return;
        }
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs }),
        });
      } catch (_) {}
    }

    async _addToCart(variantId, productHandle) {
      const journey = window.KuddyJourney;
      if (journey && typeof journey.setLastTouch === 'function') {
        await journey.setLastTouch('quiz_result', productHandle || '');
      }
      if (journey && typeof journey.trackEvent === 'function') {
        journey.trackEvent('add_to_cart', {
          source: 'quiz_result',
          product_handle: productHandle || '',
          variant_id: variantId,
          quiz_type: this.handle,
        });
      }
      const numericId = variantId.replace(/^gid:\/\/shopify\/ProductVariant\//, '');
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(numericId, 10), quantity: 1 }],
          attributes: {
            quiz_session: this.sessionId,
            quiz_type: this.handle,
            kuddy_last_touch: 'quiz_result',
            kuddy_last_touch_product: productHandle || '',
            kuddy_journey_id: journey && journey.getJourneyId ? journey.getJourneyId() : '',
          },
        }),
      });
      if (!res.ok) throw new Error('Cart error');
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
      if (typeof window.Shopify !== 'undefined' && window.Shopify.onItemAdded) {
        const data = await res.clone().json().catch(function () { return {}; });
        window.Shopify.onItemAdded(data);
      }
    }

    _funnelPayload() {
      const concerns = [];
      const goals = [];
      let skinType = '';

      if (this.answers.skin_type && this.answers.skin_type.value) {
        skinType = this.answers.skin_type.value;
      }
      if (this.answers.concern && this.answers.concern.tags) {
        concerns.push.apply(concerns, this.answers.concern.tags);
      }
      if (this.answers.hair_concern && this.answers.hair_concern.tags) {
        concerns.push.apply(concerns, this.answers.hair_concern.tags);
      }
      if (this.answers.routine_complexity && this.answers.routine_complexity.value) {
        goals.push(this.answers.routine_complexity.value);
      }
      if (this.answers.wash_frequency && this.answers.wash_frequency.value) {
        goals.push(this.answers.wash_frequency.value);
      }

      return {
        skin_type: skinType,
        concerns: concerns.filter(Boolean).filter(function (v, i, a) { return a.indexOf(v) === i; }).slice(0, 6),
        goals: goals.filter(Boolean).filter(function (v, i, a) { return a.indexOf(v) === i; }).slice(0, 4),
      };
    }

    _track(event, extra) {
      if (event === 'step') return;
      extra = extra || {};
      fetch(this.proxyBase + '/analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({
          quiz_type: this.handle,
          event_name: event,
          source: 'quiz_page',
          session_token: this.sessionId,
          journey_id: (window.KuddyJourney && window.KuddyJourney.getJourneyId) ? window.KuddyJourney.getJourneyId() : this.sessionId,
        }, extra)),
      }).catch(function () {});
    }

    _tplLoading(msg) {
      return (
        '<div class="kq-root kq-state-center">' +
          '<div class="kq-spinner" aria-hidden="true"></div>' +
          '<p class="kq-state-text">' + esc(msg) + '</p>' +
        '</div>'
      );
    }

    _tplError(msg, showRetry) {
      return (
        '<div class="kq-root kq-state-center">' +
          '<p class="kq-state-text kq-state-error">Oops &mdash; ' + esc(msg) + '</p>' +
          (showRetry
            ? '<button class="kq-btn kq-btn--outline" onclick="location.reload()" type="button">Try again</button>'
            : '') +
        '</div>'
      );
    }

    _setHTML(html) {
      this.el.innerHTML = html;
    }
  }

  function mount() {
    document.querySelectorAll('.kuddy-quiz-mount, [data-kuddy-quiz]').forEach(function (el) {
      if (!el.dataset.quizMounted) {
        el.dataset.quizMounted = '1';
        new KuddyQuiz(el).init();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
